import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useAppMessagePublication } from '@common/context/appMessagesContext';
import { useUserState } from '@common/context/userContext';
import AppMessages from '@components/AppMessages';
import ErrorBoundary from '@components/ErrorBoundary';
import Header from '@components/Header';
import Loader from '@components/Loader';
import useUserDetails from '@effects/useUserdetails';
import { useWebRouteKey } from '@effects/useWebRouteKey';
import { resolveMountCriteria } from '@utils/app-message-matcher';
import { setTokenProvider } from '@utils/axios-client';
import { AccountTypeEnum } from '@utils/enums';
import { format } from 'date-fns';
import { ComponentType, FC, useEffect } from 'react';
import { Redirect, Route, RouteProps, useHistory, useLocation } from 'react-router-dom';

export const ProtectedRoute = ({ component, path, ...args }: RouteProps) => {
    const RegisteredComponent = withUserDetails(component as ComponentType, path);
    const MyProtectedComponent = withAuthenticationRequired(RegisteredComponent as ComponentType, {
        onRedirecting: () => (
            <>
                <Loader
                    isLoading={true}
                    error={false}
                    retry={() => null}
                    timedOut={false}
                    pastDelay={false}
                />
            </>
        ),
        returnTo: args.location?.pathname,
    });
    return (
        <Route
            component={MyProtectedComponent}
            {...args}
        />
    );
};

export const AdminProtectedRoute = ({ component, path, ...args }: RouteProps) => {
    const [{ user }] = useUserState();
    if (user?.claims?.accounts?.some((acc) => acc.accountType === AccountTypeEnum.ADMIN))
        return (
            <ProtectedRoute
                component={component}
                // Forward `path` so `withUserDetails` sees the declared pattern (it compares it with
                // '/register' for the new-user redirect). Routing is unaffected either way: <Switch>
                // supplies computedMatch.
                path={path}
                {...args}
            />
        );
    else {
        return <Redirect to="/" />;
    }
};

const withUserDetails = <P extends object>(
    Component: ComponentType<P>,
    path: RouteProps['path']
): FC<P> => {
    return function WithUserDetails(props: P): JSX.Element {
        const { isAuthenticated, getAccessTokenSilently, logout, isLoading: isFetchingAccessToken } = useAuth0();
        const { isLoading, isNewUser, fetchUserDetails } = useUserDetails();
        const [{ user }, dispatch] = useUserState();
        const history = useHistory();
        const location = useLocation();

        // ENVDB-7836: the ONE place the App_Messages banner renders. This wrapper knows the route but
        // holds no consignment; pages that do (the wizard, summary, copy, device movement) publish
        // their forms and status through AppMessagesProvider, and resolveMountCriteria folds them in
        // when the publication is for this same route. Everywhere else matches on route, platform
        // and dates alone, so those rows must leave Forms and Show_For_Status blank in Airtable.
        const route = useWebRouteKey();
        const publication = useAppMessagePublication();

        if (isNewUser || user?.claims?.isNewUser) {
            if (path !== '/register') history.push('/register');
        }
        const isRegisterPage = window.location.pathname === '/register';

        useEffect(() => {
            // set up token provider for axios interceptor
            setTokenProvider(getAccessTokenSilently);
        }, [getAccessTokenSilently]);

        useEffect(() => {
            /**
             * isLoading is the flag for checking if userDetailsQuery request is processing
             * isFetchingAccessToken is the flag for checking if SSO request to MyMLA is processing
             * Only render the component and/or check the user's login status when both requests are
             * not processing
             */
            if (isLoading || isFetchingAccessToken) {
                return;
            }

            (async (): Promise<void> => {
                var accessToken;
                try {
                    accessToken = await getAccessTokenSilently();
                } catch (e) {
                    console.error('Something is wrong. no access token returned.');
                    logout({ logoutParams: { returnTo: window.location.origin + '/logout' } });
                }

                if (!accessToken) {
                    console.error('no access token returned.');
                    logout({ logoutParams: { returnTo: window.location.origin + '/logout' } });
                }

                if (accessToken) {
                    if (accessToken !== user?.token) {
                        // Signin call
                        await fetchUserDetails(accessToken);
                    }
                }

                if (!isLoading && isNewUser) {
                    if (path !== '/register') history.push('/register');
                }
            })();
        }, [
            isLoading,
            isFetchingAccessToken,
            fetchUserDetails,
            isNewUser,
            history,
            user?.token,
            dispatch,
            getAccessTokenSilently,
            logout,
        ]);

        return isFetchingAccessToken || isLoading || (isAuthenticated && !user.token) ? (
            <Loader
                isLoading={true}
                error={false}
                retry={() => null}
                timedOut={false}
                pastDelay={false}
            />
        ) : (
            <>
                {!isRegisterPage && <Header />}
                <div className={!isRegisterPage ? 'container' : ''}>
                    <div className="AppMessages-mount">
                        <AppMessages
                            criteria={resolveMountCriteria(route, publication, format(new Date(), 'yyyy-MM-dd'))}
                        />
                    </div>
                    {/* Keyed on the path so navigating away via the Header clears a caught error. */}
                    <ErrorBoundary resetKey={location.pathname}>
                        <Component {...props} />
                    </ErrorBoundary>
                </div>
                <style
                    jsx
                    global
                >
                    {`
                        .container {
                            margin-top: 50px;
                        }

                        /* A page's own top margin (e.g. SummaryPage's m-t-80) clears the fixed
                           Header for that page's content, but this mount renders before that
                           wrapper, so it would otherwise sit flush against the Header with all
                           the clearance pushed below it instead of above. This is the mount's own
                           clearance, deliberately universal rather than borrowing one page's
                           spacing value. Padding, not margin: this is the first child of the
                           container above, which has no top border or padding, so a margin here
                           would collapse into the container's own margin-top and vanish. */
                        .AppMessages-mount:not(:empty) {
                            padding-top: 24px;
                        }
                    `}
                </style>
            </>
        );
    };
};
